import React, { useCallback, useState } from 'react';
import { Query } from '../_common';
import SearchBar from '../SearchBar';
import { SearchTypeEnum } from '../../types/SearchTypeEnum';
import { ModalForm, Option } from './interface';
import { GET_ALL_CUSTOMERS_FILTERED, GET_ALL_CUSTOMER_PARENTS } from '../../graphql/client/queries';
import {
    GetAllCustomerParents,
    GetAllCustomerParents_getAllCustomerParents as CustomerParent,
    GetAllCustomersFiltered_getAllCustomersFiltered as Customer,
    GetAllCustomersFiltered,
} from '../../types/schemaTypes';
import CustomerList from './CustomerList';
import { Error, Loading } from '../_common';
import AddCustomerModal from './AddEditCustomerModal/AddEditCustomerModal';
import './Customers.scss';
import { useStoreContext } from '../App/StoreProvider';
import { useAuthContext } from '../Auth/AuthProvider';
import { PermissionEnum } from '../../types/PermissionEnum';
import { useQuery, useMutation } from '@apollo/client/react';
import { SAVE_CUSTOMER, DELETE_CUSTOMER } from '../../graphql/client/mutations';
import { SaveCustomer } from '../../types/schemaTypes';
import { Alert } from 'react-bootstrap';

import {  ToastContainer, toast } from 'react-toastify';
import {toastOpt} from '../../utils/helpers/toastHelper'

const toasterOptions = { duration: 2500 };
const Messages = {
    delete: 'Customer deleted successfully',
};
const ErrorMessages = {
    delete: 'Failed to delete Customer',
};

let selectedCustomer: Customer | undefined;

const PAGE_SIZE = 50;

const Customers = (): React.ReactElement => {
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [modalForm, setModalForm] = useState(ModalForm.EMPTY);
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [tabTitle, setTabTitle] = useState(document.title);
    document.title = 'SBS';
    const canEditCustomers = useAuthContext().userHasPermission(PermissionEnum.EditCustomer);
    const [state] = useStoreContext();
    const {
        customerList: { searchPhrase },
    } = state;

    const variables = {
        input: searchPhrase,
    };

    const onCompletedSaveCustomer = (data: SaveCustomer): void => {
        if (data.saveCustomer === null) return;
        const saveCustomerErrors = data.saveCustomer.errors;
        if (saveCustomerErrors && saveCustomerErrors.length > 0) {
            setErrorMessage(saveCustomerErrors[0] || '');
        } else {
            selectedCustomer = undefined;
            setShowAddCustomerModal(false);
        }
    };
    const renderToast = (message, type = 'info') => {
        interface ToastProps {
            onClose?: () => void;
        }
        const Comp = (props: ToastProps) => {
            return (
                <Alert variant={type} onClose={props.onClose}>
                    {message}
                </Alert>
            );
        };

        Comp.displayName = 'toast';
        return Comp;
    };
    const onCompleted = action => {
        toast.success(Messages[action],toastOpt);
        
        //refetch();
    };

    const onActionError = action => {
        toast.error(ErrorMessages[action],toastOpt);
       
        //refetch();
    };
    const [saveCustomer] = useMutation<SaveCustomer>(SAVE_CUSTOMER, {
        onCompleted: onCompletedSaveCustomer,
        refetchQueries: () => {
            return [
                {
                    query: GET_ALL_CUSTOMERS_FILTERED,
                    variables: { variables },
                },
            ];
        },
    });
    const [deleteCustomer] = useMutation(DELETE_CUSTOMER, {
        onCompleted: () => onCompleted('delete'),
        onError: () => onActionError('delete'),
        refetchQueries: () => {
            return [
                {
                    query: GET_ALL_CUSTOMERS_FILTERED,
                    variables: { variables },
                },
            ];
        },
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onDelete = useCallback(async (record: any) => {
        const variables = { input: { id: record.id } };
        await deleteCustomer({ variables });
    }, []);
    const handleOnHide = (): void => {
        selectedCustomer = undefined;
        setErrorMessage('');
        setShowAddCustomerModal(false);
    };
    const handleOnSave = async (
        id: number,
        customerName: string,
        description: string,
        enabled: boolean,
        extSystemIdentifier: string,
        industryClassificationCode: string,
        customerParentId: number,
    ): Promise<void> => {
        if (selectedCustomer) {
            const selectedCustomerMirror = {...selectedCustomer}
            selectedCustomerMirror.customerName = customerName;
            selectedCustomerMirror.description = description;
            selectedCustomerMirror.customerParentId = customerParentId;
            selectedCustomerMirror.enabled = enabled;
            selectedCustomerMirror.extSystemIdentifier = extSystemIdentifier;
            selectedCustomerMirror.industryClassificationCode = industryClassificationCode;
            selectedCustomer = {...selectedCustomerMirror}
        }

        const variables = {
            input: {
                id,
                customerName,
                description,
                customerParentId,
                enabled,
                extSystemIdentifier,
                industryClassificationCode,
            },
        };
        await saveCustomer({ variables });
    };
    const { data: customerParentsData } = useQuery<GetAllCustomerParents>(GET_ALL_CUSTOMER_PARENTS);
    const options: Option[] = [];

    if (customerParentsData && customerParentsData.getAllCustomerParents) {
        customerParentsData.getAllCustomerParents.forEach((element: CustomerParent | null) => {
            if (!element || element.id === null) return;
            options.push({ id: element.id, name: element.customerName ?? '<None>' });
        });
    }
    document.title = 'Customers';
    return (
        <Query<GetAllCustomersFiltered> query={GET_ALL_CUSTOMERS_FILTERED} variables={variables}>
            {({
                error,
                data,
                loading,
                refetch,
                fetchMore,
            }): React.ReactElement | null => {


                const offset = data && data.getAllCustomersFiltered ? data.getAllCustomersFiltered.length / PAGE_SIZE : 0;

                React.useEffect(()=>{
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults &&
                            data &&
                            data.getAllCustomersFiltered &&
                            data.getAllCustomersFiltered.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
                        setIsEndOfResults(true);
                    }
                },[data, data?.getAllCustomersFiltered?.length]);

                if (loading) {
                    return <Loading />;
                }

                if (error) {
                    return <Error error={error} />;
                }

                const customers: Customer[] = [];

                if (data && data.getAllCustomersFiltered && data.getAllCustomersFiltered.length !== 0) {
                    data.getAllCustomersFiltered.forEach((element) => {
                        if (element !== null) customers.push(element as Customer);
                    });
                }

                return (
                    <div className="Customers-Container">
                        <div className="Customers-Top-Container">
                            <div className="Customers-SearchBar">
                                <SearchBar type={SearchTypeEnum.Customer}></SearchBar>
                            </div>
                            <div className="Customers-RightOptions">
                                {canEditCustomers && (
                                    <div
                                        className="Customers-Create-New-Customer Row"
                                        onClick={() => {
                                            setShowAddCustomerModal(true);
                                            setModalForm(ModalForm.ADD_CUSTOMER);
                                        }}
                                    >
                                        <i className="material-icons Customers-Icon">add_circle</i>
                                        <span className="Customers-Link">Create New Customer</span>
                                    </div>
                                )}
                            </div>
                            <div className="Customers-Errors">{errorMessage}</div>
                        </div>
                        <div className="Customers-Bottom-Container">
                            <div className="Customers-Header-Title">Name</div>
                            <div className="Customers-Header-Title">Description</div>
                            <div className="Customers-Header-Title">JDENumber</div>
                            <div className="Customers-Header-Title">Status</div>
                            <div className="Customers-Header-Title">Actions</div>
                        </div>
                        {customers.length > 0 && (
                            <CustomerList
                                customers={customers}
                                refetch={refetch}
                                isLoading={loading}
                                canEditCustomers={canEditCustomers}
                                isEndOfResults={isEndOfResults}
                                onEditClick={Customer => {
                                    setShowAddCustomerModal(true);
                                    setModalForm(ModalForm.EDIT_CUSTOMER);
                                    selectedCustomer = Customer;
                                }}
                                onDelete={onDelete}
                                onLoadMore={() =>
                                    fetchMore({
                                        variables: {
                                            offset,
                                        },
                                        updateQuery: (prev, { fetchMoreResult }) => {
                                            if (
                                                prev &&
                                                fetchMoreResult &&
                                                prev.getAllCustomersFiltered &&
                                                fetchMoreResult.getAllCustomersFiltered
                                            ) {
                                                return Object.assign({}, prev, {
                                                    getCustomerFiltered: [
                                                        ...prev.getAllCustomersFiltered,
                                                        ...fetchMoreResult.getAllCustomersFiltered,
                                                    ],
                                                });
                                            } else {
                                                return prev;
                                            }
                                        },
                                    })
                                }
                            />
                        )}
                        <AddCustomerModal
                            show={showAddCustomerModal}
                            modalForm={modalForm}
                            options={options}
                            selectedCustomer={selectedCustomer}
                            onClickHide={handleOnHide}
                            onClickSave={handleOnSave}
                            errorMessage={errorMessage}
                        ></AddCustomerModal>
                    </div>
                );
            }}
        </Query>
    );
};

export default Customers;

